import {
  getAdminClients as apiGetAdminClients,
  getStoresData as apiGetStoresData,
  getRateCards as apiGetRateCards,
  getAdminOwnerDashboard as apiGetAdminOwnerDashboard,
  getAdminSuperDashboard as apiGetAdminSuperDashboard,
  getAdminDashboardStoresSummary as apiGetAdminDashboardStoresSummary,
  getStoreLedgerSummary as apiGetStoreLedgerSummary,
  getReportData as apiGetReportData,
  getClientDashboard as apiGetClientDashboard,
  getClientDashboardCharts as apiGetClientDashboardCharts,
  getAdminStorageFeeBreakdown as apiGetAdminStorageFeeBreakdown,
} from './api';

const DEFAULT_TTL_MS = 60 * 1000;
// Dashboard data changes slowly; 5 minutes makes returning to a dashboard
// near-instant while staying fresh enough for billing-day monitoring.
const DASHBOARD_TTL_MS = 5 * 60 * 1000;

export { DASHBOARD_TTL_MS };

const _cache = new Map();
const _inflight = new Map();
// Per-key generation counters: invalidating a key bumps its generation so an
// in-flight request started before the invalidation cannot repopulate the
// cache with stale data when it settles.
const _gen = new Map();

// ---- Persisted stale-while-revalidate layer ----
// Dashboard-related responses are mirrored into localStorage, namespaced by
// the authenticated identity (role + email), so a page reload or new tab can
// paint the last-known numbers instantly and revalidate in the background.
// The identity namespace guarantees one user's persisted cache is never read
// under another login; logout/impersonation transitions wipe it entirely.
const PERSIST_PREFIX = 'skusavvy_dcache:';
// Persisted entries older than this are pruned automatically on identity set.
const MAX_PERSIST_AGE_MS = 24 * 60 * 60 * 1000;
let _identity = '';

// Remove any persisted entries (across all identities) whose stored timestamp
// is older than MAX_PERSIST_AGE_MS. Called on every setCacheIdentity so stale
// entries from previous sessions are cleaned up without requiring a logout.
export function sweepPersistedCache(maxAgeMs = MAX_PERSIST_AGE_MS) {
  try {
    const cutoff = Date.now() - maxAgeMs;
    const doomed = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(PERSIST_PREFIX)) continue;
      try {
        const raw = localStorage.getItem(k);
        if (raw === null) continue;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed.t !== 'number' || !('v' in parsed) || parsed.t < cutoff) {
          doomed.push(k);
        }
      } catch (e) {
        // Unparseable entry — prune it too
        doomed.push(k);
      }
    }
    doomed.forEach((k) => localStorage.removeItem(k));
  } catch (e) {
    // localStorage unavailable — nothing to sweep
  }
}

export function setCacheIdentity(identity) {
  _identity = identity || '';
  sweepPersistedCache();
}

export function clearPersistedCache() {
  try {
    const doomed = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PERSIST_PREFIX)) doomed.push(k);
    }
    doomed.forEach((k) => localStorage.removeItem(k));
  } catch (e) {
    // localStorage unavailable — nothing persisted to clear
  }
}

function shouldPersist(key) {
  return (
    Boolean(_identity) &&
    (key.startsWith('dash:') || key === 'stores' || key.startsWith('adminClients:'))
  );
}

function storageKey(key) {
  return `${PERSIST_PREFIX}${_identity}:${key}`;
}

function persistEntry(key, entry) {
  if (!shouldPersist(key)) return;
  try {
    localStorage.setItem(storageKey(key), JSON.stringify({ v: entry.v, t: entry.t }));
  } catch (e) {
    // Quota exceeded / unavailable — persisted cache is best-effort only
  }
}

function readPersisted(key) {
  if (!shouldPersist(key)) return null;
  try {
    const raw = localStorage.getItem(storageKey(key));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.t !== 'number' || !('v' in parsed)) return null;
    return parsed;
  } catch (e) {
    return null;
  }
}

function removePersisted(key) {
  if (!_identity) return;
  try {
    localStorage.removeItem(storageKey(key));
  } catch (e) {}
}

// Cache key builders shared with the dashboards (for peekCache).
export const dashKeys = {
  super: (shippingWindow) => `dash:super:${shippingWindow ?? 'default'}`,
  owner: (storeId) => `dash:owner:${storeId ?? 'all'}`,
  storesSummary: () => 'dash:storesSummary',
  ledger: (storeId, range = {}) =>
    `dash:ledger:${storeId}:${range.fromDate ?? ''}:${range.toDate ?? ''}`,
  report: (storeId) => `dash:report:${storeId}`,
  client: (scopeKey) => `dash:client:${scopeKey}`,
  clientCharts: (scopeKey) => `dash:clientCharts:${scopeKey}`,
  adminClients: (storeId) => `adminClients:${storeId}`,
  storageFeeBreakdown: (storeId, month) => `dash:storageFeeBreakdown:${storeId}:${month}`,
};

// Write a value directly into the cache (and persist it). Used after a
// ?fresh=true API call to populate the cache with the freshest data.
export function setDashboardEntry(key, value) {
  const entry = { v: value, t: Date.now() };
  _cache.set(key, entry);
  persistEntry(key, entry);
}

function getEntry(key) {
  let entry = _cache.get(key);
  if (!entry) {
    entry = readPersisted(key);
    if (entry) _cache.set(key, entry);
  }
  return entry ?? null;
}

// Synchronously read the last-known value for a key (memory first, then the
// persisted layer) regardless of freshness. Used by dashboards to paint
// stale data instantly while a background revalidation runs.
export function peekCache(key) {
  const entry = getEntry(key);
  return entry ? { value: entry.v, t: entry.t } : null;
}

function currentGen(key) {
  return _gen.get(key) ?? 0;
}

function invalidateKey(key) {
  _cache.delete(key);
  _inflight.delete(key);
  removePersisted(key);
  _gen.set(key, currentGen(key) + 1);
}

function isFresh(entry, ttl) {
  return entry && Date.now() - entry.t < ttl;
}

function isErrorResponse(res) {
  return !res || res.success === false;
}

// revalidate=true skips the freshness check (forced background refresh) but
// still dedupes against an identical in-flight request.
async function cachedFetch(key, ttl, fetcher, { revalidate = false } = {}) {
  const entry = getEntry(key);
  if (!revalidate && isFresh(entry, ttl)) return entry.v;

  const inflight = _inflight.get(key);
  if (inflight) return inflight;

  const gen = currentGen(key);
  const p = (async () => {
    try {
      const res = await fetcher();
      if (!isErrorResponse(res) && currentGen(key) === gen) {
        const next = { v: res, t: Date.now() };
        _cache.set(key, next);
        persistEntry(key, next);
      }
      return res;
    } finally {
      if (_inflight.get(key) === p) _inflight.delete(key);
    }
  })();

  _inflight.set(key, p);
  return p;
}

export function getAdminClientsCached(storeId, { ttl = DEFAULT_TTL_MS, revalidate } = {}) {
  if (!storeId) return Promise.resolve({ clients: [] });
  return cachedFetch(dashKeys.adminClients(storeId), ttl, () => apiGetAdminClients(storeId), {
    revalidate,
  });
}

export function getStoresDataCached({ ttl = DEFAULT_TTL_MS, revalidate } = {}) {
  return cachedFetch('stores', ttl, () => apiGetStoresData(), { revalidate });
}

export function getRateCardsCached(storeId, { ttl = DEFAULT_TTL_MS } = {}) {
  if (!storeId) return Promise.resolve({ rateCards: [] });
  return cachedFetch(`rateCards:${storeId}`, ttl, () => apiGetRateCards(storeId));
}

// ---- Dashboard caches (short TTL, keyed by scope) ----

export function getAdminOwnerDashboardCached(
  storeId,
  { ttl = DASHBOARD_TTL_MS, revalidate, fresh } = {},
) {
  if (fresh) {
    return apiGetAdminOwnerDashboard(storeId, { fresh: true }).then((result) => {
      if (result?.success !== false) setDashboardEntry(dashKeys.owner(storeId), result);
      return result;
    });
  }
  return cachedFetch(dashKeys.owner(storeId), ttl, () => apiGetAdminOwnerDashboard(storeId), {
    revalidate,
  });
}

export function getAdminSuperDashboardCached(
  { shippingWindow } = {},
  { ttl = DASHBOARD_TTL_MS, revalidate, fresh } = {},
) {
  if (fresh) {
    return apiGetAdminSuperDashboard(
      shippingWindow ? { shippingWindow, fresh: true } : { fresh: true },
    ).then((result) => {
      if (result?.success !== false) setDashboardEntry(dashKeys.super(shippingWindow), result);
      return result;
    });
  }
  return cachedFetch(
    dashKeys.super(shippingWindow),
    ttl,
    () => apiGetAdminSuperDashboard(shippingWindow ? { shippingWindow } : {}),
    { revalidate },
  );
}

export function getAdminDashboardStoresSummaryCached({
  ttl = DASHBOARD_TTL_MS,
  revalidate,
  fresh,
} = {}) {
  if (fresh) {
    return apiGetAdminDashboardStoresSummary({ fresh: true }).then((result) => {
      if (result?.success !== false) setDashboardEntry(dashKeys.storesSummary(), result);
      return result;
    });
  }
  return cachedFetch(dashKeys.storesSummary(), ttl, () => apiGetAdminDashboardStoresSummary(), {
    revalidate,
  });
}

export function getStoreLedgerSummaryCached(
  storeId,
  range = {},
  { ttl = DASHBOARD_TTL_MS, revalidate } = {},
) {
  if (!storeId) return Promise.resolve(null);
  return cachedFetch(
    dashKeys.ledger(storeId, range),
    ttl,
    () => apiGetStoreLedgerSummary(storeId, range),
    { revalidate },
  );
}

export function getReportDataCached(storeId, { ttl = DASHBOARD_TTL_MS, revalidate } = {}) {
  if (!storeId) return Promise.resolve(null);
  return cachedFetch(dashKeys.report(storeId), ttl, () => apiGetReportData(storeId), {
    revalidate,
  });
}

export function getAdminStorageFeeBreakdownCached(
  storeId,
  month,
  { ttl = DASHBOARD_TTL_MS, revalidate } = {},
) {
  if (!storeId || !/^\d{4}-\d{2}$/.test(month)) return Promise.resolve(null);
  return cachedFetch(
    dashKeys.storageFeeBreakdown(storeId, month),
    ttl,
    () => apiGetAdminStorageFeeBreakdown(storeId, month),
    { revalidate },
  );
}

// scopeKey ties client-portal caches to the active client identity so an
// in-app account or impersonation switch never reuses another client's data.
export function getClientDashboardCached(
  scopeKey = '',
  { ttl = DASHBOARD_TTL_MS, revalidate } = {},
) {
  return cachedFetch(dashKeys.client(scopeKey), ttl, () => apiGetClientDashboard(), { revalidate });
}

export function getClientDashboardChartsCached(
  scopeKey = '',
  { ttl = DASHBOARD_TTL_MS, revalidate } = {},
) {
  return cachedFetch(dashKeys.clientCharts(scopeKey), ttl, () => apiGetClientDashboardCharts(), {
    revalidate,
  });
}

export function invalidateDashboardCache() {
  invalidateMetadataCache('dash:');
}

export function invalidateMetadataCache(prefix) {
  const keys = new Set([..._cache.keys(), ..._inflight.keys()]);
  if (_identity) {
    try {
      const identityPrefix = `${PERSIST_PREFIX}${_identity}:`;
      for (let i = 0; i < localStorage.length; i++) {
        const persistedKey = localStorage.key(i);
        if (!persistedKey?.startsWith(identityPrefix)) continue;
        const logicalKey = persistedKey.slice(identityPrefix.length);
        if (!prefix || logicalKey.startsWith(prefix)) keys.add(logicalKey);
      }
    } catch (e) {
      // localStorage unavailable — memory/in-flight invalidation still applies
    }
  }
  for (const key of keys) {
    if (!prefix || key.startsWith(prefix)) invalidateKey(key);
  }
}

export function invalidateAdminClients(storeId) {
  if (storeId) invalidateKey(`adminClients:${storeId}`);
  else invalidateMetadataCache('adminClients:');
}

export function invalidateRateCards(storeId) {
  if (storeId) invalidateKey(`rateCards:${storeId}`);
  else invalidateMetadataCache('rateCards:');
}

export function invalidateStoresData() {
  invalidateKey('stores');
}
